import { use, useEffect, useMemo, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import useTableFiltering from 'shared/components/table/hooks/table-filtering';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import ObjectSort from 'shared/models/object-sort';
import Pagination from 'shared/models/pagination';

import ReadLinkableWanderingBoundariesQuery from 'features/residents/models/read-linkable-wandering-boundaries-query';
import { WanderingBoundary } from 'features/residents/models/wandering-boundary';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';
import { useLazyReadLinkableWanderingBoundaries } from 'features/residents/resident-settings/providers/resident-queries';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLinkButtonClicked: (selectedWanderingBoundaries: WanderingBoundary[]) => void;
  wanderingBoundaryIdsToHide: string[];
};

const useWanderingBoundaryLinkModal = ({ isOpen, onClose, onLinkButtonClicked, wanderingBoundaryIdsToHide }: Props) => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const sessionStorageSortingKey = FilterSessionStorageKeys.LINKABLE_WANDERING_BOUNDARIES_SORTING;
  const searchBarSessionStorageKey = FilterSessionStorageKeys.LINKABLE_WANDERING_BOUNDARIES_SEARCHBAR;

  const [selectedWanderingBoundaries, setSelectedWanderingBoundaries] = useState<WanderingBoundary[]>([]);
  const [sortFromSessionStorage, setSortFromSessionStorage] = useSessionStorage<ObjectSort<WanderingBoundary>>(sessionStorageSortingKey, {
    property: 'name',
    isAscending: true,
  });
  const [searchBarFromSessionStorage, setSearchBarFromSessionStorage] = useSessionStorage<string>(searchBarSessionStorageKey, '');

  const {
    detailEditState: { currentResidentSetting },
  } = use(ResidentSettingsProviderContext);

  const {
    currentPagination: [currentPagination, setCurrentPagination],
    currentPage,
    currentRowsPerPage,
    setCurrentPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSearchChanged,
    resetPaging,
  } = useTableFiltering();

  const readLinkableWanderingBoundariesQuery = useMemo(() => {
    const paginationWithHiddenBoundaries = {
      skip: currentPagination.skip,
      take: currentPagination.take + wanderingBoundaryIdsToHide.length,
    } as Pagination;

    return {
      organisationUnitId: currentResidentSetting?.organisationUnitId ?? '',
      pagination: paginationWithHiddenBoundaries,
      sort: sortFromSessionStorage,
      searchQuery: searchBarFromSessionStorage,
    } as ReadLinkableWanderingBoundariesQuery;
  }, [
    currentResidentSetting?.organisationUnitId,
    currentPagination.skip,
    currentPagination.take,
    wanderingBoundaryIdsToHide.length,
    sortFromSessionStorage.property,
    sortFromSessionStorage.isAscending,
    searchBarFromSessionStorage,
  ]);

  const {
    refetch: readLinkableWanderingBoundaries,
    data: readLinkableWanderingBoundariesData,
    isSuccess: readLinkableWanderingBoundariesIsSuccess,
    isPending: readLinkableWanderingBoundariesIsLoading,
    error: readLinkableWanderingBoundariesError,
  } = useLazyReadLinkableWanderingBoundaries(readLinkableWanderingBoundariesQuery);

  const handleOnClose = () => {
    onClose();
    setSelectedWanderingBoundaries([]);
  };

  const handleOnLinkButtonClicked = () => {
    onLinkButtonClicked(selectedWanderingBoundaries);
    setSelectedWanderingBoundaries([]);
  };

  const updateSelectedWanderingBoundaries = (wanderingBoundaries: WanderingBoundary[]) => {
    setSelectedWanderingBoundaries(wanderingBoundaries);
  };

  const handleOnSortChanged = (property: NestedKeyof<WanderingBoundary>, isAscending: boolean) => {
    setSortFromSessionStorage({
      isAscending: isAscending,
      property: property,
    });
    setCurrentPage(0);
    setCurrentPagination({
      skip: 0,
      take: currentRowsPerPage,
    });
  };

  const handleOnSearchbarChanged = (searchValue: string) => {
    handleOnSearchChanged(searchValue);
    setSearchBarFromSessionStorage(searchValue);
    resetPaging();
  };

  const wanderingBoundaries =
    readLinkableWanderingBoundariesData?.wanderingBoundaries
      .filter((wanderingBoundary) => !wanderingBoundaryIdsToHide.includes(wanderingBoundary.id))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

  useEffect(() => {
    if (readLinkableWanderingBoundariesError) {
      setErrorMessage(readLinkableWanderingBoundariesError);
    }
  }, [readLinkableWanderingBoundariesError]);

  useEffect(() => {
    if (isOpen) {
      readLinkableWanderingBoundaries();
      setSelectedWanderingBoundaries([]);
    }
  }, [readLinkableWanderingBoundariesQuery, isOpen]);

  return {
    selectedWanderingBoundaries,
    updateSelectedWanderingBoundaries,
    handleOnClose,
    handleOnLinkButtonClicked,

    wanderingBoundaries,
    totalWanderingBoundaries: readLinkableWanderingBoundariesData?.total ?? 0,
    readLinkableWanderingBoundariesIsLoading,
    readLinkableWanderingBoundariesIsSuccess,

    currentPage,
    currentRowsPerPage,
    handleOnPageChanged,
    handleOnRowsPerPageChanged,
    handleOnSortChanged,
    handleOnSearchbarChanged,
    searchBarFromSessionStorage,
    sortFromSessionStorage,
  };
};

export default useWanderingBoundaryLinkModal;
